/**
 * Tour id -> definition. A dynamic import, so the sales story is a chunk only
 * a demo visitor ever downloads (spec 25 section 5.2).
 */
const LOADERS = {
  sales: () => import('./definitions/sales.js'),
  // Spec 33: the task walkthroughs, one chunk each.
  'create-load': () => import('./definitions/create-load.js'),
  assign: () => import('./definitions/assign.js'),
  track: () => import('./definitions/track.js'),
  bill: () => import('./definitions/bill.js'),
  documents: () => import('./definitions/documents.js'),
  // Spec 52: delivered to paid, on the seeded stuck load.
  collect: () => import('./definitions/collect.js'),
  // Spec 53: Dispatch a load, chapter 1, and chapters 5 to 7.
  'can-we-take-it': () => import('./definitions/can-we-take-it.js'),
  'message-driver': () => import('./definitions/message-driver.js'),
  'share-tracking': () => import('./definitions/share-tracking.js'),
  deliver: () => import('./definitions/deliver.js'),
  // Spec 53: Bill it and get paid, chapters 1 to 3.
  invoice: () => import('./definitions/invoice.js'),
  'billing-packet': () => import('./definitions/billing-packet.js'),
  'get-paid': () => import('./definitions/get-paid.js'),
  // Spec 53: the Your company group's admin setup modules.
  'add-customer': () => import('./definitions/add-customer.js'),
  'add-team-member': () => import('./definitions/add-team-member.js'),
  'add-equipment': () => import('./definitions/add-equipment.js'),
  'retire-equipment': () => import('./definitions/retire-equipment.js'),
  // Spec 53: the accountant's office modules, outside the courses.
  'connect-books': () => import('./definitions/connect-books.js'),
  'send-document': () => import('./definitions/send-document.js'),
  'fuel-and-miles': () => import('./definitions/fuel-and-miles.js'),
  // Spec 53: optional walkthroughs, More dispatching and Your company.
  'time-off': () => import('./definitions/time-off.js'),
  'add-location': () => import('./definitions/add-location.js'),
  'add-driver': () => import('./definitions/add-driver.js'),
  // Spec 53: optional walkthroughs on the practice fleet's own stages.
  'rate-con-load': () => import('./definitions/rate-con-load.js'),
  'settle-pay': () => import('./definitions/settle-pay.js'),
  'defect-to-work-order': () => import('./definitions/defect-to-work-order.js'),
};

export const TOUR_IDS = Object.freeze(Object.keys(LOADERS));

/**
 * What the walkthrough menu lists (spec 33 section 7.2), in order. Kept here
 * rather than read from the definitions so the menu does not pull every
 * walkthrough's chunk into the bundle that every page loads; the contract
 * test holds each entry to its definition's own title, summary and minutes.
 * Only walkthroughs a demo visitor may run are listed: a definition for
 * training alone (`audience: ['training']`) is started by "Practice".
 */
export const WALKTHROUGHS = Object.freeze([
  {
    id: 'create-load',
    title: 'Create a load',
    summary: 'Customer, stops, times and equipment, typed in by you, then find it on the board.',
    minutes: 3,
  },
  {
    id: 'assign',
    title: 'Assign a driver and a truck',
    summary: 'See who is free for your load, then give it a driver and a truck.',
    minutes: 2,
  },
  {
    id: 'track',
    title: 'Track it live',
    summary: 'The truck on the road, its estimate, and arrivals that suggest themselves.',
    minutes: 2,
  },
  {
    id: 'bill',
    title: 'Bill it',
    summary: 'Delivered, invoiced, billed, and synced to your books.',
    minutes: 3,
  },
  {
    id: 'documents',
    title: 'Send a document',
    summary: 'From a template to a signer, up to the point the demo stops.',
    minutes: 2,
  },
  {
    id: 'message-driver',
    title: 'Message the driver',
    summary: 'Write to the driver on your load from the office, and see where their reply lands.',
    minutes: 3,
  },
  {
    id: 'collect',
    title: 'From delivered to paid',
    summary:
      'Find what a delivered load is waiting on, finish its packet, invoice it, and watch the payment.',
    minutes: 3,
  },
]);

/**
 * Who a definition is for (spec 53 section 4.1): 'demo' (an anonymous demo
 * visitor), 'training' (a trainee in their practice fleet), or both. A
 * definition that does not say is the demo's, as every one was before.
 */
export const AUDIENCES = Object.freeze(['demo', 'training']);

export function audienceOf(definition) {
  const named = definition && Array.isArray(definition.audience) ? definition.audience : null;
  return named && named.length ? named.filter(a => AUDIENCES.includes(a)) : ['demo'];
}

export async function loadTour(id) {
  const load = Object.prototype.hasOwnProperty.call(LOADERS, id) ? LOADERS[id] : null;
  if (!load) return null;
  const mod = await load();
  return mod.default || null;
}
